/**
 * Scoped automations — the "approve once, runs on autopilot" registry.
 *
 * Each automation is ONE narrow, named, revocable behavior the operator
 * turned on from a Heartbeat proposal (src/lib/agent/heartbeat.ts) — never
 * a global autonomy switch. Enabled automations are persisted in their own
 * `automations` collection (same generic store as every other collection),
 * so they are visible, pausable, and survive reloads.
 *
 * Execution rides the EXISTING live-schedule ticker: `automationsTick` is
 * called from `liveScheduleTick` (src/lib/agent/useLiveSchedule.ts), so
 * there is no second scheduler. On its cadence each enabled automation
 * runs its draft generation — REUSING the draft-first composers
 * (src/lib/agent/draftFirst.ts) and the weekly-update agent — and QUEUES
 * `send_drafted_email` / `send_customer_update` proposals into the
 * approvals inbox, plus one honest feed entry with the run's real counts.
 * Autopilot here means "drafts appear for your one-click approve": an
 * automation NEVER writes CRM data or the outbox directly.
 *
 * Heartbeat's own once-a-day / 14-day-dismissal state persists as one
 * synthetic org-scoped `brainFacts` row (the exact flag pattern
 * src/lib/onboarding.ts uses) — no new store methods needed for it.
 */

import { db, nowIso } from '../db/store';
import { queueApproval, listApprovals } from './approvals';
import { appendFeedEntry, runSummary, type RunStats } from './activityFeed';
import { composePastDueReminder, composeReengagement } from './draftFirst';
import { resolveCustomerUpdateRecipient } from '../cs/weeklyUpdate';
import { proposeWeeklyUpdates } from './weeklyUpdateAgent';
import { runContentAutopilotFromStore } from '../content/contentAutopilot';
import type { AutomationId } from './heartbeat';
import { sameLocalDay } from './heartbeat';
import type { BillingDate, BrainFact, Person } from '../db/types';
import type { SendDraftedEmailAction } from './types';

const DAY_MS = 86_400_000;

/** Reminders draft only for invoices at least this many days past due. */
export const PAST_DUE_DRAFT_THRESHOLD_DAYS = 30;
/** Re-engagement drafts only for follow-ups at least this many days quiet. */
export const FOLLOWUP_QUIET_DAYS = 14;
/** The follow-up sweep runs from this local hour onward. */
export const FOLLOWUP_SWEEP_HOUR = 8;

// ---------------------------------------------------------------------------
// Registry
// ---------------------------------------------------------------------------

/** One persisted, scoped automation. `id` is deterministic (the def id). */
export interface AutomationRecord {
  id: AutomationId;
  name: string;
  enabled: boolean;
  enabledAt: string | null;
  lastRunAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AutomationDef {
  id: AutomationId;
  name: string;
  cadenceLabel: string;
}

export const AUTOMATION_DEFS: AutomationDef[] = [
  {
    id: 'past-due-reminders',
    name: 'Past-due payment reminders',
    cadenceLabel: 'Every day',
  },
  {
    id: 'followup-sweep',
    name: 'Daily 8am re-engagement sweep',
    cadenceLabel: 'Every day · 8am',
  },
  {
    id: 'weekly-updates',
    name: 'Monday weekly customer updates',
    cadenceLabel: 'Every Monday',
  },
  {
    id: 'content-autopilot',
    name: 'Content autopilot',
    cadenceLabel: 'Every day',
  },
];

/** The default-enabled drafting automation's id, in one place. */
export const CONTENT_AUTOPILOT_ID: AutomationId = 'content-autopilot';

/**
 * Register the DEFAULT-ENABLED automations. Content autopilot drafts —
 * it never sends/publishes — so it runs without opt-in ceremony: the
 * first tick creates its record enabled. Creation-only by contract: an
 * operator's Pause (enabled:false) is NEVER overridden here.
 */
export async function ensureDefaultAutomations(): Promise<void> {
  const existing = await db.get('automations', CONTENT_AUTOPILOT_ID);
  if (existing) return; // a paused record stays paused
  const ts = nowIso();
  await db.put('automations', {
    id: CONTENT_AUTOPILOT_ID,
    name: automationDef(CONTENT_AUTOPILOT_ID).name,
    enabled: true,
    enabledAt: ts,
    lastRunAt: null,
    createdAt: ts,
    updatedAt: ts,
  });
}

/** Look up a def; throws for an unknown id (a programming error). */
export function automationDef(id: AutomationId): AutomationDef {
  const def = AUTOMATION_DEFS.find((d) => d.id === id);
  if (!def) throw new Error(`Unknown automation ${id}`);
  return def;
}

export async function listAutomations(): Promise<AutomationRecord[]> {
  return db.list('automations');
}

export async function enabledAutomations(): Promise<AutomationRecord[]> {
  return (await db.list('automations')).filter((a) => a.enabled);
}

export function subscribeAutomations(
  fn: (records: AutomationRecord[]) => void,
): () => void {
  return db.subscribe('automations', fn);
}

/**
 * Turn ONE automation on ("Approve once"). Upserts the deterministic
 * record and appends the honest feed line — the operator still approves
 * every send the automation drafts.
 */
export async function enableAutomation(id: AutomationId): Promise<AutomationRecord> {
  const def = automationDef(id);
  const ts = nowIso();
  const existing = await db.get('automations', id);
  const record: AutomationRecord = {
    id,
    name: def.name,
    enabled: true,
    enabledAt: ts,
    lastRunAt: existing?.lastRunAt ?? null,
    createdAt: existing?.createdAt ?? ts,
    updatedAt: ts,
  };
  await db.put('automations', record);
  await appendFeedEntry({
    agentId: `automation:${id}`,
    agentName: def.name,
    summary: `Automation on: ${def.name} — you approve every send`,
    proposalsQueued: 0,
    trigger: 'manual',
    ts,
  }).catch(() => undefined);
  return record;
}

/** Pause a running automation — visible, revocable, one click. */
export async function pauseAutomation(id: AutomationId): Promise<AutomationRecord | null> {
  const existing = await db.get('automations', id);
  if (!existing) return null;
  const ts = nowIso();
  const updated = await db.update('automations', id, {
    enabled: false,
    updatedAt: ts,
  });
  await appendFeedEntry({
    agentId: `automation:${id}`,
    agentName: existing.name,
    summary: `Automation paused: ${existing.name}`,
    proposalsQueued: 0,
    trigger: 'manual',
    ts,
  }).catch(() => undefined);
  return updated;
}

// ---------------------------------------------------------------------------
// Due math (pure) — piggybacks on the live ticker's cadence, no own timer
// ---------------------------------------------------------------------------

/**
 * Is this automation due at `now`? Each runs at most once per local
 * calendar day; the follow-up sweep additionally waits for 8am, and the
 * weekly composer only fires on Mondays.
 */
export function isAutomationDue(
  id: AutomationId,
  lastRunAt: string | null,
  now: number,
): boolean {
  if (lastRunAt) {
    const t = Date.parse(lastRunAt);
    if (!Number.isNaN(t) && sameLocalDay(t, now)) return false;
  }
  const d = new Date(now);
  switch (id) {
    case 'past-due-reminders':
      return true;
    case 'followup-sweep':
      return d.getHours() >= FOLLOWUP_SWEEP_HOUR;
    case 'weekly-updates':
      return d.getDay() === 1; // Monday
    case 'content-autopilot':
      return true; // daily — the once-per-local-day guard above gates it
  }
}

// ---------------------------------------------------------------------------
// Run bodies — draft generation only; every artifact QUEUES for approval
// ---------------------------------------------------------------------------

/** Whole days a date is past `now`; ≤ 0 = not yet. */
function daysPast(iso: string, now: number): number {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return 0;
  return Math.floor((now - t) / DAY_MS);
}

/** Person ids with a pending drafted-email proposal of `intent` — de-dupe. */
async function pendingDraftIds(
  intent: SendDraftedEmailAction['intent'],
): Promise<Set<string>> {
  const pending = await listApprovals('pending');
  return new Set(
    pending
      .filter(
        (a) =>
          a.action.kind === 'send_drafted_email' &&
          (a.action as SendDraftedEmailAction).intent === intent,
      )
      .map((a) => (a.action as SendDraftedEmailAction).personId),
  );
}

/** Worst unpaid invoice at least `PAST_DUE_DRAFT_THRESHOLD_DAYS` overdue. */
function worstDeepOverdueInvoice(person: Person, now: number): BillingDate | undefined {
  return (person.billingDates ?? [])
    .filter(
      (bd) =>
        (bd.status ?? 'scheduled') !== 'paid' &&
        daysPast(bd.date, now) >= PAST_DUE_DRAFT_THRESHOLD_DAYS,
    )
    .sort((a, b) => daysPast(b.date, now) - daysPast(a.date, now))[0];
}

async function runPastDueReminders(now: number): Promise<RunStats> {
  const people = await db.list('people');
  const alreadyQueued = await pendingDraftIds('payment_reminder');
  let found = 0;
  let queued = 0;
  for (const p of people) {
    if (p.category !== 'customer' || p.status === 'churned') continue;
    const worst = worstDeepOverdueInvoice(p, now);
    if (!worst) continue;
    found++;
    if (alreadyQueued.has(p.id)) continue;
    const draft = composePastDueReminder(p, worst, now);
    if (!draft) continue;
    const recipient = resolveCustomerUpdateRecipient(p);
    if (!recipient) continue; // no address on file → never a guessed send
    const days = daysPast(worst.date, now);
    const action: SendDraftedEmailAction = {
      kind: 'send_drafted_email',
      personId: p.id,
      personName: p.name,
      intent: draft.intent,
      to: recipient.email,
      subject: draft.subject,
      text: draft.text,
      html: draft.html,
    };
    await queueApproval(action, 'scheduler', `Past-due reminder automation`, {
      rationale: `${worst.label} is ${days} days past due — the day-30 threshold you approved.`,
      evidence: [
        `${worst.label} due ${worst.date} · ${days} days past due`,
        ...(worst.amount != null ? [`$${worst.amount.toLocaleString('en-US')} outstanding`] : []),
      ],
    });
    queued++;
  }
  return { scanned: people.length, found, queued };
}

async function runFollowupSweep(now: number): Promise<RunStats> {
  const people = await db.list('people');
  const alreadyQueued = await pendingDraftIds('re_engagement');
  let found = 0;
  let queued = 0;
  for (const p of people) {
    if (!p.followUpDate) continue;
    if (p.stage === 'closed-lost' || p.status === 'churned') continue;
    const quiet = daysPast(p.followUpDate, now);
    if (quiet < FOLLOWUP_QUIET_DAYS) continue;
    found++;
    if (alreadyQueued.has(p.id)) continue;
    const draft = composeReengagement(
      p,
      { followUpDate: p.followUpDate, nextStep: p.nextStep },
      now,
    );
    if (!draft) continue;
    const recipient = resolveCustomerUpdateRecipient(p);
    if (!recipient) continue;
    const action: SendDraftedEmailAction = {
      kind: 'send_drafted_email',
      personId: p.id,
      personName: p.name,
      intent: draft.intent,
      to: recipient.email,
      subject: draft.subject,
      text: draft.text,
      html: draft.html,
    };
    await queueApproval(action, 'scheduler', `Re-engagement sweep automation`, {
      rationale: `Follow-up ${quiet} days quiet — past the 14-day sweep threshold you approved.`,
      evidence: [`Follow-up was due ${p.followUpDate} · ${quiet} days ago`],
    });
    queued++;
  }
  return { scanned: people.length, found, queued };
}

async function runWeeklyUpdates(now: number): Promise<RunStats> {
  const [people, deliverables, tickets, tasks] = await Promise.all([
    db.list('people'),
    db.list('deliverables'),
    db.list('tickets'),
    db.list('tasks'),
  ]);
  // Reuse the EXISTING weekly-update agent wholesale: one composer, one
  // action shape, one approval gate, its own cadence/pending de-dupe.
  const result = await proposeWeeklyUpdates(people, { deliverables, tickets, tasks }, now);
  return {
    scanned: people.length,
    found: result.proposed + result.skipped + result.needsEmail.length,
    queued: result.proposed,
  };
}

/**
 * Run ONE automation now: generate drafts, queue approvals, stamp
 * `lastRunAt`, and append an honest feed entry with the run's real counts.
 * Exported for tests and for a future "Run now" affordance.
 */
export async function runAutomationOnce(
  id: AutomationId,
  now: number = Date.now(),
): Promise<RunStats> {
  const def = automationDef(id);

  // Content autopilot drafts content items (never approvals) and writes
  // its OWN single feed line — "drafted N posts — M scored 80+", or the
  // honest setup ask on an empty Brain — so it is self-reporting here.
  let stats: RunStats;
  let selfReported = false;
  if (id === 'content-autopilot') {
    const result = await runContentAutopilotFromStore(now);
    stats = {
      scanned: result.seeds.length,
      found: result.drafted + result.skippedDuplicates,
      queued: 0, // drafts land in the pipeline — nothing awaits approval
    };
    selfReported = true;
  } else {
    stats =
      id === 'past-due-reminders'
        ? await runPastDueReminders(now)
        : id === 'followup-sweep'
          ? await runFollowupSweep(now)
          : await runWeeklyUpdates(now);
  }

  const ts = new Date(now).toISOString();
  const existing = await db.get('automations', id);
  if (existing) {
    await db.update('automations', id, { lastRunAt: ts, updatedAt: ts });
  }
  if (!selfReported) {
    await appendFeedEntry({
      agentId: `automation:${id}`,
      agentName: def.name,
      summary: runSummary(stats),
      proposalsQueued: stats.queued,
      trigger: 'schedule',
      ts,
    }).catch(() => undefined);
  }
  return stats;
}

/** One tick result — which automation ran and what it really did. */
export interface AutomationRunEvent {
  id: AutomationId;
  stats: RunStats;
}

/**
 * One ticker pass over the enabled automations — called from
 * `liveScheduleTick`, so automations ride the EXISTING 60s loop rather
 * than a second scheduler. A failing automation is swallowed so it can't
 * stop the rest of the sweep. Paused (enabled:false) automations never run.
 */
export async function automationsTick(now: number = Date.now()): Promise<AutomationRunEvent[]> {
  // Content autopilot is DEFAULT-ENABLED (drafting is safe — nothing
  // sends/publishes): the first tick registers it. Creation-only, so an
  // operator's Pause survives every subsequent tick.
  await ensureDefaultAutomations().catch(() => undefined);
  const enabled = await enabledAutomations();
  const events: AutomationRunEvent[] = [];
  for (const rec of enabled) {
    if (!isAutomationDue(rec.id, rec.lastRunAt, now)) continue;
    try {
      events.push({ id: rec.id, stats: await runAutomationOnce(rec.id, now) });
    } catch {
      /* swallow — one failing automation must not stop the sweep */
    }
  }
  return events;
}

// ---------------------------------------------------------------------------
// Heartbeat state — one synthetic org-scoped brainFacts row (flag pattern
// identical to src/lib/onboarding.ts; no new store methods)
// ---------------------------------------------------------------------------

/** Synthetic account id — Heartbeat is org-wide, not per-account. */
export const HEARTBEAT_ACCOUNT_ID = '__org__';
/** Deterministic doc id so every write upserts the one state row. */
export const HEARTBEAT_FACT_ID = `${HEARTBEAT_ACCOUNT_ID}:rules:heartbeatState`;

/** What Heartbeat remembers between days. */
export interface HeartbeatState {
  /** When the last proposal surfaced (drives "one per day"). */
  lastProposedAt: string | null;
  /** Which proposal surfaced — lets the card re-show it the same day. */
  lastProposedId: AutomationId | null;
  /** "Not now" stamps: automation id → dismissed-at ISO (14-day window). */
  dismissed: Record<string, string>;
}

const EMPTY_STATE: HeartbeatState = {
  lastProposedAt: null,
  lastProposedId: null,
  dismissed: {},
};

export async function readHeartbeatState(): Promise<HeartbeatState> {
  const fact = await db.get('brainFacts', HEARTBEAT_FACT_ID);
  if (!fact) return { ...EMPTY_STATE, dismissed: {} };
  try {
    const parsed = JSON.parse(fact.value) as Partial<HeartbeatState>;
    return {
      lastProposedAt: parsed.lastProposedAt ?? null,
      lastProposedId: parsed.lastProposedId ?? null,
      dismissed: parsed.dismissed ?? {},
    };
  } catch {
    return { ...EMPTY_STATE, dismissed: {} };
  }
}

async function writeHeartbeatState(state: HeartbeatState): Promise<void> {
  const existing = await db.get('brainFacts', HEARTBEAT_FACT_ID);
  const fact: BrainFact = {
    id: HEARTBEAT_FACT_ID,
    accountId: HEARTBEAT_ACCOUNT_ID,
    section: 'rules',
    field: 'heartbeatState',
    label: 'Heartbeat proposal state',
    value: JSON.stringify(state),
    source: 'founder',
    confidence: 1,
    version: (existing?.version ?? 0) + 1,
    setBy: 'heartbeat',
    setAt: nowIso(),
  };
  await db.put('brainFacts', fact);
}

/** Stamp today's surfaced proposal (enforces "at most one per day"). */
export async function markHeartbeatProposed(
  id: AutomationId,
  at: string,
): Promise<void> {
  const state = await readHeartbeatState();
  await writeHeartbeatState({ ...state, lastProposedAt: at, lastProposedId: id });
}

/** "Not now": suppress this proposal id for 14 days. */
export async function dismissHeartbeatProposal(
  id: AutomationId,
  at: string,
): Promise<void> {
  const state = await readHeartbeatState();
  await writeHeartbeatState({
    ...state,
    dismissed: { ...state.dismissed, [id]: at },
  });
}
